import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { z } from 'zod'

import CopyButtonWithLabel from '@/common/CopyButtonWithLabel'
import GradientWrapper from '@/common/GradientWrapper'
import { downloadJson } from '@/helpers'
import ProposalPreview from '@/pages/Multisig/components/ProposalPreview'
import { useSubmitMultisigProposal } from '@/pages/Multisig/CreateProposal/hooks/submit-multisig-proposal'
import { UiButton } from '@/ui/UiButton'
import { UiInput } from '@/ui/UiInput'
import { UiLabel } from '@/ui/UiLabel'

import {
  buildRemoveTxFromStopListMessage,
  RemoveTxFromStopListFormValues,
} from './stoplistMessageBuilder'

const removeTxFromStopListSchema = z.object({
  creator: z.string().min(1, 'Creator address is required'),
  chainId: z.string().min(1, 'Chain ID is required'),
  txHash: z.string().min(1, 'Transaction hash is required'),
  txNonce: z
    .string()
    .min(1, 'Transaction nonce is required')
    .refine(value => !isNaN(Number(value)) && Number(value) >= 0, {
      message: 'Nonce must be a valid non-negative number',
    }),
})

export default function RemoveTxFromStopListForm() {
  const { account } = useParams<{ account: string }>()

  const form = useForm<RemoveTxFromStopListFormValues>({
    resolver: zodResolver(removeTxFromStopListSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    defaultValues: {
      creator: '',
      chainId: '',
      txHash: '',
      txNonce: '',
    },
  })

  const submitProposalTx = useSubmitMultisigProposal({ group: account ?? '' })

  useEffect(() => {
    form.setValue('creator', account ?? '', { shouldValidate: true })
  }, [account, form])

  const watchedValues = useWatch({ control: form.control })

  const messagePayload = useMemo(() => {
    if (!form.formState.isValid || !watchedValues) return null

    try {
      return buildRemoveTxFromStopListMessage(
        watchedValues as RemoveTxFromStopListFormValues,
      )
    } catch {
      return null
    }
  }, [form.formState.isValid, watchedValues])

  const generatedJson = useMemo(() => {
    if (!messagePayload) return null
    return JSON.stringify(messagePayload, null, 2)
  }, [messagePayload])

  const isSubmitting = submitProposalTx.isPending
  const isSubmitDisabled = !form.formState.isValid || isSubmitting

  return (
    <div className='space-y-6'>
      <div className='space-y-2'>
        <h3 className='text-foreground text-lg font-semibold'>
          Remove Transaction from Stop List
        </h3>
        <p className='text-muted-foreground text-sm'>
          Fill in the form to remove a transaction from the stop list
        </p>
      </div>

      <form
        onSubmit={event => event.preventDefault()}
        className='flex flex-col gap-6'
      >
        <div className='flex flex-col gap-3'>
          <UiLabel htmlFor='creator'>Creator Address</UiLabel>
          <UiInput
            id='creator'
            {...form.register('creator')}
            placeholder='Connect wallet'
            readOnly
            disabled={isSubmitting}
            className='bg-input border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
          />
          {form.formState.errors.creator && (
            <p className='text-destructive text-sm'>
              {form.formState.errors.creator.message}
            </p>
          )}
        </div>

        <div className='border-border space-y-3 rounded-lg border p-3'>
          <h4 className='text-foreground font-medium'>
            Transaction Identification
          </h4>

          <div className='mt-5 flex flex-col gap-3'>
            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='chainId'>Chain ID</UiLabel>
              <GradientWrapper>
                <UiInput
                  id='chainId'
                  {...form.register('chainId')}
                  placeholder='ethereum'
                  disabled={isSubmitting}
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
              {form.formState.errors.chainId && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.chainId.message}
                </p>
              )}
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='txHash'>Transaction Hash</UiLabel>
              <GradientWrapper>
                <UiInput
                  id='txHash'
                  {...form.register('txHash')}
                  placeholder='0x...'
                  disabled={isSubmitting}
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
              {form.formState.errors.txHash && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.txHash.message}
                </p>
              )}
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='txNonce'>Transaction Nonce</UiLabel>
              <GradientWrapper>
                <UiInput
                  id='txNonce'
                  {...form.register('txNonce')}
                  placeholder='0'
                  disabled={isSubmitting}
                  type='number'
                  min='0'
                  step='1'
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
              {form.formState.errors.txNonce && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.txNonce.message}
                </p>
              )}
            </div>
          </div>
        </div>
      </form>

      {messagePayload && <ProposalPreview messages={messagePayload.messages} />}

      <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <div className='flex flex-wrap items-center gap-2'>
          {generatedJson && (
            <>
              <CopyButtonWithLabel
                value={generatedJson}
                label='Copy JSON'
                copiedLabel='Copied'
                tooltip='Copy JSON'
              />
              <UiButton
                type='button'
                variant='outline'
                size='sm'
                onClick={() =>
                  downloadJson(
                    messagePayload,
                    'remove-tx-from-stoplist-message.json',
                  )
                }
                disabled={isSubmitting}
              >
                Download JSON
              </UiButton>
            </>
          )}
        </div>
        <UiButton
          type='button'
          disabled={isSubmitDisabled}
          onClick={form.handleSubmit(values => {
            const messageObject = buildRemoveTxFromStopListMessage(values)
            submitProposalTx.mutate({
              rawJsonText: JSON.stringify(messageObject, null, 2),
            })
          })}
        >
          {isSubmitting ? 'Submitting…' : 'Submit proposal'}
        </UiButton>
      </div>
    </div>
  )
}
